import { type Expense, type OcrResult } from "@finway-group/shared/lib/models"
import { ExpenseDocumentTypeEnum } from "@finway-group/shared/lib/models/expense/expenseDocumentType.enum"
import { Modal } from "antd"
import { t } from "i18next"
import { useCallback } from "react"
import { type ExpenseFormContextInterface } from "~Components/modals/expenseCreateForm.context"
import { defaultFilterInvoiceLink } from "~Features/pages/purchaseOrders/queries/const"
import { fetchPurchaseOrdersForLinking } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { isLineItemSplit } from "~Shared/utils/expense.utils"
import { type OcrSettingsWithSetters } from "~Shared/utils/ocr.utils.types"

/** @returns the purchase order number to select from the OCR result, if any */
export const getOcrPurchaseOrderNumber = (expense: Expense, ocrResult: OcrResult | null, isOcrItemizationEnabled: boolean) => {
    if (!isLineItemSplit(expense) || !isOcrItemizationEnabled || ocrResult?.splits?.length === 0) {
        return undefined
    }
    if ([expense.documentType, ocrResult?.documentType].includes(ExpenseDocumentTypeEnum.CREDIT_NOTE)) {
        return undefined
    }
    return ocrResult?.purchaseOrderNumber
}

export const useExpenseOCRPOMatching = (
    expense: Expense,
    ocrSettings: OcrSettingsWithSetters,
    setSelectedPurchaseOrders: ExpenseFormContextInterface[2]["setSelectedPurchaseOrders"],
    preSelectedPurchaseOrder?: ExpenseFormContextInterface[2]["preSelectedPurchaseOrder"],
) => {
    /** @returns `false` if the user cancelled adding the document after being warned about number mismatches, `true` otherwise. */
    const updatePOByOCR = useCallback(
        async (updatedOcrObject: OcrResult | null) => {
            const purchaseOrderNumber = getOcrPurchaseOrderNumber(expense, updatedOcrObject, ocrSettings.isOcrItemizationEnabled)
            if (!purchaseOrderNumber) {
                return true
            }

            return new Promise<boolean>((resolve) => {
                const loadAndSelectPO = async () => {
                    const result = await fetchPurchaseOrdersForLinking({ filter: { ...defaultFilterInvoiceLink, purchaseOrderNumber: { eq: purchaseOrderNumber } } })

                    if (result?.docs?.length > 0) {
                        const purchaseOrder = result.docs[0]
                        setSelectedPurchaseOrders([
                            {
                                _id: purchaseOrder._id,
                                purchaseOrderNumber: purchaseOrder.purchaseOrderNumber,
                                alreadyLinked: false,
                            },
                        ])
                    }
                    resolve(true)
                }

                if (preSelectedPurchaseOrder && preSelectedPurchaseOrder.purchaseOrderNumber !== purchaseOrderNumber) {
                    Modal.confirm({
                        title: t("confirm:purchase_order.link_mismatching_invoice.title"),
                        content: t("confirm:purchase_order.link_mismatching_invoice.message", {
                            poNumberOnInvoice: purchaseOrderNumber,
                            poNumber: preSelectedPurchaseOrder.purchaseOrderNumber,
                        }),
                        okText: t("confirm:purchase_order.link_mismatching_invoice.confirm"),
                        cancelText: t("confirm:purchase_order.link_mismatching_invoice.cancel"),
                        onOk: loadAndSelectPO,
                        onCancel() {
                            resolve(false)
                        },
                    })
                } else {
                    void loadAndSelectPO()
                }
            })
        },
        [expense, ocrSettings.isOcrItemizationEnabled, setSelectedPurchaseOrders, preSelectedPurchaseOrder],
    )
    return {
        updatePOByOCR,
    }
}
